import React, { useMemo, useCallback } from 'react';
import { Form } from 'react-final-form';
import {
    Button,
    Divider,
    Grid,
    IconButton,
    makeStyles,
    Typography,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ExportButton from 'components/ExportButton';
import dayjs from 'dayjs';
import { useHistory } from 'react-router-dom';
import { DatePicker, TextField } from 'components/form';
import Layout from 'modules/layout';
import { requestHistoricalExport, pollExportStatus, downloadHistoricalExport } from 'modules/api/usageAnalytics';
import { useSelector } from 'react-redux';
import { isClient } from 'modules/auth/selectors';
import { ROUTE_USAGE_DETAIL } from 'app/routes';
import sleep from 'utils/sleep';

const SHORTCUTS = [
    [
        { label: 'Šiandiena', from: () => dayjs(), to: () => dayjs() },
        { label: 'Ši savaitė', from: () => dayjs().startOf('week'), to: () => dayjs() },
        { label: 'Šis mėnuo', from: () => dayjs().startOf('month'), to: () => dayjs().endOf('month') },
    ],
    [
        { label: 'Praeitas mėnuo', from: () => dayjs().subtract(1, 'month').startOf('month'), to: () => dayjs().subtract(1, 'month').endOf('month') },
        { label: 'Praeitas ir šis mėnuo', from: () => dayjs().subtract(1, 'month').startOf('month'), to: () => dayjs().endOf('month') },
        { label: 'Šie metai', from: () => dayjs().startOf('year'), to: () => dayjs() },
        { label: 'Praeiti metai', from: () => dayjs().subtract(1, 'year').startOf('year'), to: () => dayjs().subtract(1, 'year').endOf('year') },
    ],
];

const useStyles = makeStyles((theme) => ({
    wrapper: {
        maxWidth: 700,
        margin: '0 auto',
        paddingTop: theme.spacing(2),
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(4),
    },
    backBtn: {
        padding: 4,
        color: '#111',
    },
    title: {
        fontSize: 28,
        fontWeight: 700,
        color: '#111',
    },
    dateRow: {
        marginBottom: theme.spacing(2),
    },
    shortcutsBlock: {
        marginBottom: theme.spacing(3),
    },
    shortcutRow: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(0.5),
    },
    shortcutBtn: {
        fontSize: 13,
        color: theme.palette.primary.main,
        textTransform: 'none',
        padding: '2px 8px',
        minWidth: 0,
        '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
    },
    divider: {
        height: 14,
        alignSelf: 'center',
        margin: '0 2px',
    },
    fieldsRow: {
        marginBottom: theme.spacing(2),
    },
    submitRow: {
        display: 'flex',
        justifyContent: 'center',
    },
}));

function applyShortcut(form, shortcut) {
    form.change('dateFrom', shortcut.from().toISOString());
    form.change('dateTo', shortcut.to().toISOString());
}

export default function HistoricalData() {
    const classes = useStyles();
    const history = useHistory();
    const isClientAdmin = useSelector(isClient);

    const initialValues = useMemo(
        () => ({
            dateFrom: dayjs().startOf('month').toISOString(),
            dateTo: dayjs().toISOString(),
            clientName: '',
            clientDbName: '',
            service: '',
            callerIdentifier: '',
        }),
        [],
    );

    const validateForm = useCallback((values) => {
        const errors = {};
        const from = values.dateFrom ? dayjs(values.dateFrom) : null;
        const to = values.dateTo ? dayjs(values.dateTo) : null;
        if (from && to && to.diff(from, 'day') > 365) {
            errors.dateTo = 'Data negali viršyti 1 metų intervalo';
        }
        return errors;
    }, []);

    const handleSubmit = useCallback(async (values) => {
        const payload = {
            dateFrom: values.dateFrom ? dayjs(values.dateFrom).format('YYYY-MM-DD') : null,
            dateTo: values.dateTo ? dayjs(values.dateTo).format('YYYY-MM-DD') : null,
            filters: {
                ...(values.clientName ? { clientName: values.clientName } : {}),
                ...(values.clientDbName ? { clientDbName: values.clientDbName } : {}),
                ...(values.service ? { service: values.service } : {}),
                ...(values.callerIdentifier ? { callerIdentifier: values.callerIdentifier } : {}),
            },
        };
        try {
            const { jobId } = await requestHistoricalExport(payload);
            const MAX_POLLS = 90; // 90 × 2 s = 3 min ceiling
            for (let i = 0; i < MAX_POLLS; i++) {
                await sleep(2000);
                const job = await pollExportStatus(jobId);
                if (job.status === 'DONE') {
                    await downloadHistoricalExport(jobId, job.filename);
                    return;
                }
                if (job.status === 'ERROR') {
                    return { FORM_ERROR: job.errorMessage || 'Klaida eksportuojant duomenis' };
                }
            }
            return { FORM_ERROR: 'Eksportas užtruko per ilgai. Pabandykite su mažesniu laikotarpiu.' };
        } catch (e) {
            return { FORM_ERROR: e.message || 'Klaida eksportuojant duomenis' };
        }
    }, []);

    return (
        <Layout>
            <div className={classes.titleRow}>
                <IconButton
                    className={classes.backBtn}
                    onClick={() => history.push(ROUTE_USAGE_DETAIL.path)}
                    aria-label="Atgal"
                >
                    <ArrowBackIcon />
                </IconButton>
                <Typography className={classes.title}>Istoriniai duomenys</Typography>
            </div>
            <div className={classes.wrapper}>
                <Form
                    onSubmit={handleSubmit}
                    validate={validateForm}
                    initialValues={initialValues}
                    render={({ handleSubmit: submit, form, submitting, submitError, errors, touched }) => (
                        <form onSubmit={submit}>
                            {/* Date range */}
                            <Grid container spacing={2} className={classes.dateRow}>
                                <Grid item xs={6}>
                                    <DatePicker
                                        name="dateFrom"
                                        label="Data nuo"
                                        inputVariant="outlined"
                                        variant="inline"
                                        format="YYYY-MM-DD"
                                        size="small"
                                        fullWidth
                                        readOnly={false}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <DatePicker
                                        name="dateTo"
                                        label="Data iki"
                                        inputVariant="outlined"
                                        variant="inline"
                                        format="YYYY-MM-DD"
                                        size="small"
                                        fullWidth
                                        readOnly={false}
                                        helperText={touched?.dateTo && errors?.dateTo}
                                    />
                                </Grid>
                            </Grid>

                            {/* Quick shortcuts */}
                            <div className={classes.shortcutsBlock}>
                                {SHORTCUTS.map((row, ri) => (
                                    <div key={ri} className={classes.shortcutRow}>
                                        {row.map((s, si) => (
                                            <React.Fragment key={s.label}>
                                                {si > 0 && (
                                                    <Divider
                                                        orientation="vertical"
                                                        flexItem
                                                        className={classes.divider}
                                                    />
                                                )}
                                                <Button
                                                    className={classes.shortcutBtn}
                                                    disableRipple
                                                    onClick={() => applyShortcut(form, s)}
                                                >
                                                    {s.label}
                                                </Button>
                                            </React.Fragment>
                                        ))}
                                    </div>
                                ))}
                            </div>

                            {/* Filters */}
                            <Grid container spacing={2} className={classes.fieldsRow}>
                                {!isClientAdmin && (
                                    <Grid item xs={6}>
                                        <TextField
                                            name="clientName"
                                            label="Klientas"
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            readOnly={false}
                                        />
                                    </Grid>
                                )}
                                <Grid item xs={6}>
                                    <TextField
                                        name="clientDbName"
                                        label="Duomenų bazė"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        readOnly={false}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        name="service"
                                        label="Service"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        readOnly={false}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        name="callerIdentifier"
                                        label="Identifikatorius"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        readOnly={false}
                                    />
                                </Grid>
                            </Grid>

                            {/* Submit */}
                            {submitError && (
                                <Typography
                                    style={{ color: '#c62828', fontSize: 13, textAlign: 'center', marginBottom: 12 }}
                                >
                                    {submitError}
                                </Typography>
                            )}
                            <div className={classes.submitRow}>
                                <ExportButton type="submit" loading={submitting} />
                            </div>
                        </form>
                    )}
                />
            </div>
        </Layout>
    );
}
